import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  personalInfo,
  frontendSkills,
  backendSkills,
  systemSkills,
  projects,
  education,
} from "@/mocks/devData";

gsap.registerPlugin(ScrollTrigger);

const clientProjects = projects.filter((p) => p.isClient);
const featuredProjects = [
  ...clientProjects,
  ...projects.filter((p) => !p.isClient).slice(0, 3),
];

const allSkillNames = [
  ...frontendSkills.map((s) => s.name),
  ...backendSkills.map((s) => s.name),
  ...systemSkills.map((s) => s.name),
];

const experience = [
  {
    role: "Frontend Developer",
    company: "Venture IT",
    period: "2026 — Present",
    type: "Part-time / Freelance",
    points: [
      "Developed Authentic Touch e-commerce platform with Next.js, Sanity CMS & ShadCn UI",
      "Built Time Ex Tours travel agency website with dynamic content management",
      "Collaborated with design & backend teams to deliver pixel-perfect interfaces",
    ],
  },
  {
    role: "Web Developer",
    company: "Daak Chithi ORG",
    period: "",
    type: "Freelance",
    points: [
      "Created the full organizational website using React JS & Material Tailwind",
      "Implemented React Router for seamless page navigation & routing",
    ],
  },
  {
    role: "Web Developer",
    company: "Team Bertho ORG",
    period: "",
    type: "Freelance",
    points: [
      "Built the team organization website from scratch with React JS",
      "Designed responsive UI components using Material Tailwind & Tailwind CSS",
    ],
  },
];

export default function ResumePage() {
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.9, ease: "power3.out", delay: 0.1 }
      );
      const sections = pageRef.current?.querySelectorAll(".resume-section");
      sections?.forEach((sec) => {
        gsap.fromTo(sec, { opacity: 0, y: 30 }, {
          opacity: 1, y: 0, duration: 0.7, ease: "power3.out",
          scrollTrigger: { trigger: sec, start: "top 88%", once: true },
        });
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="min-h-screen text-white" style={{ background: "#080808", fontFamily: "'Space Grotesk', sans-serif" }}>

      {/* Subtle grid */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* ── NAVBAR ── */}
      <header
        className="sticky top-0 z-50"
        style={{ background: "rgba(8,8,8,0.97)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        {/* Main navbar row */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">

          {/* Back button */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-200 transition-colors duration-200 cursor-pointer flex-shrink-0"
          >
            <i className="ri-arrow-left-line text-sm" />
            <span className="text-sm font-mono hidden sm:inline">Back to Portfolio</span>
            <span className="text-sm font-mono sm:hidden">Back</span>
          </button>

          {/* Center — logo/title */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className="text-sm font-black tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#e5e7eb" }}
            >
              HR <span style={{ color: "#4ade80", filter: "brightness(0.65) saturate(0.7)" }}>Safin</span>
            </span>
            <span className="text-gray-800 hidden sm:inline">·</span>
            <div className="hidden sm:flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse" />
              <span className="text-[11px] font-mono text-gray-600 tracking-widest uppercase">Resume</span>
            </div>
          </div>

          {/* Right — actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Desktop hire me */}
            <a
              href={`mailto:${personalInfo.email}`}
              className="btn-3d btn-3d-ghost hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-mono"
            >
              <i className="ri-mail-line text-xs" />
              Hire Me
            </a>

            {/* Mobile email icon */}
            <a
              href={`mailto:${personalInfo.email}`}
              className="sm:hidden w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-200 transition-colors cursor-pointer"
              style={{ border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <i className="ri-mail-line text-sm" />
            </a>

            {/* Mobile menu toggle */}
            <button
              className="sm:hidden w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-200 transition-colors cursor-pointer"
              style={{ border: "1px solid rgba(255,255,255,0.08)" }}
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
            >
              <i className={`${mobileNavOpen ? "ri-close-line" : "ri-menu-2-line"} text-sm`} />
            </button>
          </div>
        </div>

        {/* Mobile dropdown nav */}
        {mobileNavOpen && (
          <div
            className="sm:hidden border-t px-4 py-3 flex flex-col gap-1"
            style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(8,8,8,0.98)" }}
          >
            {[
              { label: "Experience", icon: "ri-briefcase-line" },
              { label: "Projects", icon: "ri-folder-3-line" },
              { label: "Education", icon: "ri-graduation-cap-line" },
              { label: "Skills", icon: "ri-code-s-slash-line" },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  setMobileNavOpen(false);
                  const el = document.getElementById(`resume-${item.label.toLowerCase()}`);
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all duration-150 cursor-pointer text-sm font-mono text-left"
              >
                <i className={`${item.icon} text-gray-600`} />
                {item.label}
              </button>
            ))}
            <div className="mt-1 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              <a
                href={`mailto:${personalInfo.email}`}
                className="btn-3d btn-3d-primary w-full py-2.5 text-sm justify-center"
              >
                <i className="ri-mail-line" /> Hire Me
              </a>
            </div>
          </div>
        )}
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">

        {/* ── HEADER ── */}
        <div ref={headerRef} className="mb-12 md:mb-16">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            {/* Photo */}
            <div className="flex-shrink-0">
              <div
                className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-2xl overflow-hidden flex items-center justify-center"
                style={{
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "linear-gradient(160deg, #0d1f0e 0%, #071407 60%, #0a1a0b 100%)",
                }}
              >
                <img
                  src="https://i.postimg.cc/nc3fKCwH/myimage-removebg-preview-(1).png"
                  alt={personalInfo.name}
                  className="w-full h-full object-contain object-center"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-[10px] font-mono text-gray-600 tracking-[0.4em] uppercase">Available for work</span>
                <div className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse" />
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white mb-1 leading-tight">
                {personalInfo.name}
              </h1>
              <p className="text-gray-400 font-medium text-base mb-3">{personalInfo.title}</p>
              <p className="text-gray-600 text-sm leading-relaxed max-w-2xl mb-4 hidden sm:block">{personalInfo.bio}</p>

              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                <a href={`mailto:${personalInfo.email}`} className="flex items-center gap-1.5 text-[11px] font-mono text-gray-600 hover:text-gray-300 transition-colors cursor-pointer">
                  <i className="ri-mail-line" /><span className="truncate max-w-[160px] sm:max-w-none">{personalInfo.email}</span>
                </a>
                <span className="text-gray-800 hidden sm:inline">·</span>
                <span className="flex items-center gap-1.5 text-[11px] font-mono text-gray-600">
                  <i className="ri-map-pin-line" />{personalInfo.location}
                </span>
                <span className="text-gray-800 hidden sm:inline">·</span>
                <a href={personalInfo.github} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[11px] font-mono text-gray-600 hover:text-gray-300 transition-colors cursor-pointer">
                  <i className="ri-github-fill" />GitHub
                </a>
                <span className="text-gray-800 hidden sm:inline">·</span>
                <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[11px] font-mono text-gray-600 hover:text-gray-300 transition-colors cursor-pointer">
                  <i className="ri-linkedin-box-fill" />LinkedIn
                </a>
              </div>
            </div>
          </div>
          <div className="mt-6 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">

          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-2 flex flex-col gap-10">

            {/* Experience */}
            <section id="resume-experience" className="resume-section">
              <SectionTitle icon="ri-briefcase-line" label="Work Experience" />
              <div className="flex flex-col gap-5 mt-5">
                {experience.map((exp, i) => (
                  <div
                    key={i}
                    className="rounded-xl p-4 sm:p-5 transition-all duration-300 hover:-translate-y-0.5"
                    style={{ background: "#111", border: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                      <div>
                        <h3 className="text-white font-semibold text-sm sm:text-base">{exp.role}</h3>
                        <p className="text-gray-400 text-sm font-mono">{exp.company}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {exp.period && (
                          <span className="text-[11px] font-mono text-gray-600">{exp.period}</span>
                        )}
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af" }}
                        >{exp.type}</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5">
                      {exp.points.map((pt, j) => (
                        <li key={j} className="flex items-start gap-2 text-gray-600 text-[13px] leading-relaxed">
                          <i className="ri-arrow-right-s-line text-gray-700 mt-0.5 flex-shrink-0" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Projects */}
            <section id="resume-projects" className="resume-section">
              <SectionTitle icon="ri-folder-3-line" label="Featured Projects" />
              <div className="flex flex-col gap-3 mt-5">
                {featuredProjects.map((proj) => (
                  <div
                    key={proj.id}
                    className="rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 group"
                    style={{ background: "#111", border: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    <div
                      className="h-[1px]"
                      style={{
                        background: proj.isClient
                          ? "linear-gradient(90deg, rgba(22,163,74,0.4), transparent)"
                          : "linear-gradient(90deg, rgba(255,255,255,0.08), transparent)",
                      }}
                    />
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-gray-200 font-semibold text-sm group-hover:text-white transition-colors duration-200">{proj.title}</h3>
                            {proj.isClient && (
                              <span
                                className="text-[9px] font-mono px-1.5 py-0.5 rounded-full flex-shrink-0"
                                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af" }}
                              >CLIENT</span>
                            )}
                          </div>
                          <p className="text-gray-700 text-[11px] font-mono mt-0.5">{proj.category}</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {proj.live && (
                            <a
                              href={proj.live}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/5 transition-colors duration-200"
                              style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                            >
                              <i className="ri-external-link-line text-gray-500 text-xs" />
                            </a>
                          )}
                          {proj.github && (
                            <a
                              href={proj.github}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/5 transition-colors duration-200"
                              style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                            >
                              <i className="ri-github-fill text-gray-600 text-xs" />
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {proj.tech.slice(0, 4).map((t) => (
                          <span
                            key={t}
                            className="text-[10px] font-mono px-2 py-0.5 rounded"
                            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", color: "#6b7280" }}
                          >{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] font-mono text-gray-700 text-center pt-1">
                  + {projects.length - featuredProjects.length} more on portfolio
                </p>
              </div>
            </section>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="flex flex-col gap-8">

            {/* Education */}
            <section id="resume-education" className="resume-section">
              <SectionTitle icon="ri-graduation-cap-line" label="Education" />
              <div className="flex flex-col gap-3 mt-5">
                {education.map((edu) => (
                  <div
                    key={edu.id}
                    className="rounded-xl p-4"
                    style={{ background: "#111", border: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <h3 className="text-gray-300 font-semibold text-sm leading-snug">{edu.degree}</h3>
                      <span
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.07)",
                          color: edu.status === "Currently Pursuing" ? "#86efac" : "#6b7280",
                        }}
                      >
                        {edu.status === "Currently Pursuing" ? "● Active" : "✓ Done"}
                      </span>
                    </div>
                    <p className="text-gray-600 text-[12px] font-mono mb-2">{edu.institution}</p>
                    {edu.status === "Currently Pursuing" && (
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-[10px] font-mono text-gray-700">Progress</span>
                          <span className="text-[10px] font-mono text-gray-600">80%</span>
                        </div>
                        <div className="h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.04)" }}>
                          <div className="h-full rounded-full" style={{ width: "80%", background: "rgba(22,163,74,0.5)" }} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Skills */}
            <section id="resume-skills" className="resume-section">
              <SectionTitle icon="ri-code-s-slash-line" label="Technical Skills" />
              <div className="mt-5 space-y-4">
                <SkillGroup label="Frontend" skills={frontendSkills.map((s) => s.name)} />
                <SkillGroup label="Backend" skills={backendSkills.map((s) => s.name)} />
                <SkillGroup label="Tools" skills={systemSkills.map((s) => s.name)} />
              </div>
            </section>

            {/* Stats */}
            <section className="resume-section">
              <SectionTitle icon="ri-bar-chart-line" label="At a Glance" />
              <div className="grid grid-cols-2 gap-2.5 mt-5">
                {[
                  { value: "18+", label: "Projects", icon: "ri-folder-3-line" },
                  { value: `${clientProjects.length}`, label: "Client Works", icon: "ri-user-star-line" },
                  { value: `${allSkillNames.length}+`, label: "Technologies", icon: "ri-stack-line" },
                  { value: "2+", label: "Years Exp.", icon: "ri-calendar-line" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-xl p-3 text-center"
                    style={{ background: "#111", border: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                      <i className={`${stat.icon} text-gray-600 text-sm`} />
                    </div>
                    <div className="text-xl font-black text-gray-200">{stat.value}</div>
                    <div className="text-[10px] font-mono text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Roles */}
            <section className="resume-section">
              <SectionTitle icon="ri-user-line" label="Roles" />
              <div className="flex flex-col gap-1.5 mt-5">
                {personalInfo.roles.map((role) => (
                  <div
                    key={role}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                  >
                    <div className="w-1 h-1 rounded-full bg-gray-600 flex-shrink-0" />
                    <span className="text-gray-500 text-sm">{role}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {/* Footer bar */}
        <div
          className="mt-14 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="flex items-center gap-3">
            <i className="ri-terminal-line text-gray-700 text-sm" />
            <span className="text-[11px] font-mono text-gray-700">
              <span className="text-gray-600">$ </span>
              safin.status = &quot;open to full-time &amp; freelance&quot;;
            </span>
          </div>
          <a
            href={`mailto:${personalInfo.email}`}
            className="btn-3d btn-3d-primary flex items-center gap-2 px-5 py-2 text-sm w-full sm:w-auto justify-center"
          >
            <i className="ri-mail-send-line text-xs" />
            Let&apos;s Work Together
          </a>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
      >
        <i className={`${icon} text-gray-400 text-sm`} />
      </div>
      <h2 className="text-sm font-bold text-gray-400 tracking-widest uppercase">{label}</h2>
      <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
    </div>
  );
}

function SkillGroup({ label, skills }: { label: string; skills: string[] }) {
  return (
    <div>
      <p className="text-[10px] font-mono text-gray-700 tracking-widest uppercase mb-2">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {skills.map((skill) => (
          <span
            key={skill}
            className="text-[11px] font-mono px-2 py-1 rounded-md"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", color: "#6b7280" }}
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
}
